import { GeoCoordinates } from './geo-coordinates';
import { distanceKm } from './geo-distance';

export const DISTANCE_KM_OPTIONS = [10, 25, 50, 100, 200] as const;

export function parseMaxDistanceKm(value: string | number | null | undefined): number | null {
  if (value == null || value === '') {
    return null;
  }
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function withinMaxDistanceKm(
  user: GeoCoordinates | null | undefined,
  point: GeoCoordinates | null,
  maxKm: number | null,
): boolean {
  if (maxKm == null) {
    return true;
  }
  if (!user) {
    return true;
  }
  if (!point) {
    return false;
  }
  return distanceKm(user, point) <= maxKm;
}

export function matchesOrganizationName(
  organizationName: string | null | undefined,
  filter: string | null | undefined,
): boolean {
  const query = (filter ?? '').trim().toLowerCase();
  if (query.length === 0) {
    return true;
  }
  return (organizationName ?? '').toLowerCase().includes(query);
}

export function extractTableSearchableStrings(table: {
  tableName?: string | null;
  description?: string | null;
  isOnline?: boolean | null;
  playedGame?: {
    gameName?: string | null;
    gameDescription?: string | null;
    description?: string | null;
    gameType?: string | null;
    publisher?: string | null;
  } | null;
  event?: {
    name?: string | null;
    description?: string | null;
    organization?: { organizationName?: string | null } | null;
  } | null;
  organization?: { organizationName?: string | null } | null;
  location?: {
    formalName?: string | null;
    displayName?: string | null;
    city?: string | null;
    town?: string | null;
    village?: string | null;
    road?: string | null;
    state?: string | null;
    county?: string | null;
    province?: string | null;
    country?: string | null;
    address?: { street?: string; city?: string; town?: string; village?: string; province?: string; country?: string } | string | null;
  } | null;
  creator?: {
    username?: string | null;
    userName?: string | null;
    name?: string | null;
    surname?: string | null;
    bio?: string | null;
  } | null;
}): string[] {
  const locationName =
    table.location?.formalName ??
    table.location?.displayName ??
    table.location?.city ??
    table.location?.town ??
    table.location?.village ??
    '';

  const addressStrings: string[] = [];
  if (typeof table.location?.address === 'string') {
    addressStrings.push(table.location.address);
  } else if (table.location?.address && typeof table.location.address === 'object') {
    const addr = table.location.address;
    if (addr.street) addressStrings.push(addr.street);
    if (addr.city) addressStrings.push(addr.city);
    if (addr.town) addressStrings.push(addr.town);
    if (addr.village) addressStrings.push(addr.village);
    if (addr.province) addressStrings.push(addr.province);
    if (addr.country) addressStrings.push(addr.country);
  }

  const strings: (string | null | undefined)[] = [
    table.tableName,
    table.description,
    table.playedGame?.gameName,
    table.playedGame?.gameDescription,
    table.playedGame?.description,
    table.playedGame?.gameType,
    table.playedGame?.publisher,
    table.event?.name,
    table.event?.description,
    table.organization?.organizationName,
    table.event?.organization?.organizationName,
    locationName,
    table.location?.formalName,
    table.location?.displayName,
    table.location?.city,
    table.location?.town,
    table.location?.village,
    table.location?.road,
    table.location?.state,
    table.location?.county,
    table.location?.province,
    table.location?.country,
    ...addressStrings,
    table.isOnline ? 'Online' : 'In presenza',
    table.creator?.userName,
    table.creator?.username,
    table.creator?.name,
    table.creator?.surname,
    table.creator?.bio,
  ];

  return strings
    .filter((s): s is string => typeof s === 'string' && s.trim().length > 0)
    .map((s) => s.trim());
}

export function matchesTableGlobalSearch(
  table: Parameters<typeof extractTableSearchableStrings>[0],
  query: string | null | undefined,
): boolean {
  const trimmed = (query ?? '').trim().toLowerCase();
  if (trimmed.length === 0) {
    return true;
  }
  const searchableStrings = extractTableSearchableStrings(table);
  const words = trimmed.split(/\s+/).filter((word) => word.length > 0);
  return words.every((word) =>
    searchableStrings.some((field) => field.toLowerCase().includes(word)),
  );
}

export function extractLocationDisplayName(
  location: {
    formalName?: string | null;
    displayName?: string | null;
    city?: string | null;
    town?: string | null;
    village?: string | null;
  } | null | undefined,
  fallback = 'Online',
): string {
  return (
    location?.formalName ??
    location?.displayName ??
    location?.city ??
    location?.town ??
    location?.village ??
    fallback
  );
}

export function extractEventSearchableStrings(event: {
  name?: string | null;
  description?: string | null;
  organization?: { organizationName?: string | null } | null;
  location?: {
    formalName?: string | null;
    displayName?: string | null;
    city?: string | null;
    town?: string | null;
    village?: string | null;
    road?: string | null;
    state?: string | null;
    county?: string | null;
    province?: string | null;
    country?: string | null;
    address?: { street?: string; city?: string; town?: string; village?: string; province?: string; country?: string } | string | null;
  } | null;
  userProfile?: {
    username?: string | null;
    userName?: string | null;
    name?: string | null;
    surname?: string | null;
    bio?: string | null;
  } | null;
  gameTables?: Array<{
    tableName?: string | null;
    description?: string | null;
    playedGame?: { gameName?: string | null } | null;
  }> | null;
}): string[] {
  const locationName = extractLocationDisplayName(event.location, '');

  const addressStrings: string[] = [];
  if (typeof event.location?.address === 'string') {
    addressStrings.push(event.location.address);
  } else if (event.location?.address && typeof event.location.address === 'object') {
    const addr = event.location.address;
    if (addr.street) addressStrings.push(addr.street);
    if (addr.city) addressStrings.push(addr.city);
    if (addr.town) addressStrings.push(addr.town);
    if (addr.village) addressStrings.push(addr.village);
    if (addr.province) addressStrings.push(addr.province);
    if (addr.country) addressStrings.push(addr.country);
  }

  const tableStrings: string[] = [];
  if (event.gameTables && Array.isArray(event.gameTables)) {
    for (const table of event.gameTables) {
      if (table.tableName) tableStrings.push(table.tableName);
      if (table.description) tableStrings.push(table.description);
      if (table.playedGame?.gameName) tableStrings.push(table.playedGame.gameName);
    }
  }

  const strings: (string | null | undefined)[] = [
    event.name,
    event.description,
    event.organization?.organizationName,
    locationName,
    event.location?.formalName,
    event.location?.displayName,
    event.location?.city,
    event.location?.town,
    event.location?.village,
    event.location?.road,
    event.location?.state,
    event.location?.county,
    event.location?.province,
    event.location?.country,
    ...addressStrings,
    event.userProfile?.userName,
    event.userProfile?.username,
    event.userProfile?.name,
    event.userProfile?.surname,
    event.userProfile?.bio,
    ...tableStrings,
  ];

  return strings
    .filter((s): s is string => typeof s === 'string' && s.trim().length > 0)
    .map((s) => s.trim());
}

export function matchesEventGlobalSearch(
  event: Parameters<typeof extractEventSearchableStrings>[0],
  query: string | null | undefined,
): boolean {
  const trimmed = (query ?? '').trim().toLowerCase();
  if (trimmed.length === 0) {
    return true;
  }
  const searchableStrings = extractEventSearchableStrings(event);
  const words = trimmed.split(/\s+/).filter((word) => word.length > 0);
  return words.every((word) =>
    searchableStrings.some((field) => field.toLowerCase().includes(word)),
  );
}
